import { useTranslation } from 'react-i18next';

import { gtmBasicEvent } from '@klv/shared/utils/google-tag-manager';
import { generateClassName } from '@klv/shared/utils/functions';

import * as s from './styles';
import type { IPlanCard } from './types';
import { PricingCalculator } from '../pricing-calculator';

const PlanCard = ({
  buttonLink,
  buttonOpenInNewTab,
  buttonText = 'Sign up',
  children,
  contacts,
  contactsString,
  contactLimit,
  copy,
  displayMobilePush = true,
  id,
  emailSends,
  freeCredits,
  name,
  price,
  showDetails = false,
  topCalloutText = '',
  smsCredits = 0,
  isSms,
  setIsSms,
  selectedLocale,
  setSelectedLocale,
  backgroundColor = 'light',
  ...props
}: IPlanCard) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const handleClick = () => {
    if (buttonText === (t('organisms_pricing_signUp') || 'Sign up')) {
      // We have a sign up button click, fire off a Data Layer event
      gtmBasicEvent({
        category: 'pricing card',
        action: 'signup cta',
        label: name.toLowerCase(),
      });
    }
    return true;
  };

  return (
    <s.StyledPlanCard hasCalloutText={!!(topCalloutText)} id={id} {...props}>
      <s.CardContents>
        <s.PlanName forwardedAs="h3" variant="h6Semibold">{name}</s.PlanName>
        <s.PriceEyebrow forwardedAs="div" variant="tagline">{t('organisms_pricing_startingAt')}</s.PriceEyebrow>
        <s.PriceWrapper aria-live="polite">
          <s.Amount content={`${new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price)}`} tag="div" styledAs="display3" />
          <s.TimePeriod forwardedAs="div" variant="tagline">/ {t('organisms_pricing_month')}</s.TimePeriod>
        </s.PriceWrapper>
        <s.Copy forwardedAs="p" variant="paragraph2">{copy}</s.Copy>
        <s.ButtonContainer>
          <s.CTA openInNewTab={buttonOpenInNewTab || undefined} text={buttonText} action={handleClick} url={buttonLink || undefined} />
          {contacts > 10000 && <s.DemoButton text={t('organisms_navigation_dataActions_getADemo_button_label')} url={t('organisms_navigation_dataActions_getADemo_button_href')} variant="ghost" buttonColor="dark" />}
        </s.ButtonContainer>
        <s.Divider />
        <s.PlanFeatures aria-live="polite">
          <s.Feature>
            {(contactLimit && contacts > contactLimit) ? (
              <s.Text $bold forwardedAs="span" variant="tagline">{t('organisms_pricing_contactLimitExceeded')}</s.Text>
            ) : (
              <>
                <s.Text $bold forwardedAs="span" variant="tagline">{contactsString}</s.Text>
                {' '}
                <s.Text forwardedAs="span" variant="tagline">{t('organisms_pricing_contacts')}</s.Text>
              </>
            )}
          </s.Feature>
          <s.Feature>
            <s.Text forwardedAs="span" variant="tagline">{t('organisms_pricing_startingAt')}</s.Text>
            {' '}
            <s.Text $bold forwardedAs="span" variant="tagline"></s.Text>
            {new Intl.NumberFormat(locale).format(emailSends)}
            {' '}
            <s.Text forwardedAs="span" variant="tagline">{t('organisms_pricing_monthlyEmailSends')}</s.Text>
          </s.Feature>
          {displayMobilePush && <s.Feature><s.Text forwardedAs="span" variant="tagline">{t('organisms_pricing_mobilePush')}</s.Text></s.Feature>}
          {freeCredits && <s.Feature><s.Text forwardedAs="span" variant="tagline">{`${new Intl.NumberFormat(locale).format(freeCredits)} ${t('organisms_pricing_freeCredits')}`}</s.Text></s.Feature>}
          <s.Feature><s.Text forwardedAs="span" variant="tagline">{t('organisms_pricing_includesFees')}</s.Text></s.Feature>
          <s.Feature><s.Text forwardedAs="span" variant="tagline">{(price > 0) ? t('organisms_pricing_emailAndChatSupport') : t('organisms_pricing_freeSupport')}</s.Text></s.Feature>
        </s.PlanFeatures>
        {children && <s.PlanChildren>{children}</s.PlanChildren>}
        {!!smsCredits && (
          <PricingCalculator
            backgroundColor={backgroundColor}
            smsCredits={smsCredits}
            isSms={isSms}
            setIsSms={setIsSms}
            selectedLocale={selectedLocale}
            setSelectedLocale={setSelectedLocale}
          />
        )}
      </s.CardContents>
      {showDetails && (
        <s.PlanDetails>
          <s.Text $bold forwardedAs="p" variant="tagline">{t('organisms_pricing_supportToScale')}</s.Text>
          <s.Text forwardedAs="p" variant="tagline">{t('organisms_pricing_onboarding')}</s.Text>
          <s.Text forwardedAs="p" variant="tagline">{t('organisms_pricing_csm')}</s.Text>
        </s.PlanDetails>
      )}
    </s.StyledPlanCard>
  );
};

export default PlanCard;
